import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { CONFIG, authHeaders, authFetch,getCookie } from '../utils/config';

const GS1DigitalLinkEditor = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    baseUrl: 'https://id.gs1.org',
    gtin: '',
    lot: '',
    exp: '',
    lang: '',
    linkType: '',
    maxRedirect: '',
    validUntil: ''
  });

  const [generatedUrl, setGeneratedUrl] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [currentUuid, setCurrentUuid] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const callGS1API = async (formData) => {
    const params = new URLSearchParams();
    if (formData.baseUrl) params.append('base_url', formData.baseUrl);
    if (formData.gtin) params.append('gtin', formData.gtin);
    if (formData.lot) params.append('lot', formData.lot);
    if (formData.exp) params.append('exp', formData.exp);
    if (formData.lang) params.append('lang', formData.lang);
    if (formData.linkType) params.append('link_type', formData.linkType);

    const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/gs1/generate?${params.toString()}`, {
      method: 'GET',
      headers: authHeaders({
        'Content-Type': 'application/json'
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur API ${response.status}: ${errorText}`);
    }

    return await response.json();
  };

  const generateQRCode = async (url, maxRedirect = '', validUntil = '') => {
    const qrData = {
      'redirect_url': url,
      'dots-color': '#000000',
      'background-color': '#ffffff',
      'box-size': 10,
      'border-size': 4,
      'dots-shape': 'square',
      'dot-radius': 0,
      'dot-spacing': 0
    };

    if (maxRedirect && maxRedirect.trim() !== '') {
      qrData['max_redirect'] = parseInt(maxRedirect);
    }

    if (validUntil && validUntil.trim()) {
      const date = new Date(validUntil);
      qrData['valid_until'] = date.toISOString().replace('.000Z', '+00:00');
    }

    const requestData = {
      ...qrData,
      'content': qrData['redirect_url'],
      'retrieve-mode': 'base64'
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(requestData));

    const response = await authFetch(`${CONFIG.BASE_URL}/preview-classic-qr-code`, {
      method: 'POST',
      headers: authHeaders(false),
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur API ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    
    if (result['qr-code-data']) {
      return {
        qr_code_url: `data:image/png;base64,${result['qr-code-data']}`
      };
    } else {
      throw new Error(result.message || 'Erreur lors de la génération du QR code');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const ensureHttps = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('http://')) return 'https://' + trimmed.slice(7);
    return 'https://' + trimmed;
  };

  const handleBaseUrlBlur = () => {
    if (formData.baseUrl.trim()) {
      setFormData(prev => ({ ...prev, baseUrl: ensureHttps(prev.baseUrl) }));
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!formData.baseUrl.trim()) {
      setError('L\'URL de base est requise');
      return;
    }

    const token = getCookie('quikr_access_token') || localStorage.getItem('quikr_access_token');
    if (!token) {
      setError('Session expirée. Veuillez vous reconnecter.');
      navigate('/login', { state: { from: { pathname: '/dashboard/gs1' } } });
      return;
    }

    setIsGenerating(true);
    setError('');
    
    try {
      const linkResponse = await callGS1API(formData);
      setGeneratedUrl(linkResponse.generated_url);
      
      const qrResponse = await generateQRCode(linkResponse.generated_url, formData.maxRedirect, formData.validUntil);
      setQrCodeUrl(qrResponse.qr_code_url);
      
      const qrData = {
        'redirect_url': linkResponse.generated_url,
        'dots-color': '#000000',
        'background-color': '#ffffff',
        'box-size': 10,
        'border-size': 4,
        'dots-shape': 'square',
        'dot-radius': 0,
        'dot-spacing': 0,
        'item_type': 'gs1'
      };

      if (formData.maxRedirect && formData.maxRedirect.trim() !== '') {
        qrData['max_redirect'] = parseInt(formData.maxRedirect);
      }

      if (formData.validUntil && formData.validUntil.trim()) {
        const date = new Date(formData.validUntil);
        qrData['valid_until'] = date.toISOString().replace('.000Z', '+00:00');
      }

      qrData.content = qrData['redirect_url'];

      const formDataForDownload = new FormData();
      formDataForDownload.append('data', JSON.stringify(qrData));

      const downloadResponse = await authFetch(`${CONFIG.BASE_URL}/qr-code`, {
        method: 'POST',
        headers: authHeaders(false),
        body: formDataForDownload
      });

      if (downloadResponse.ok) {
        const downloadJson = await downloadResponse.json();
        if (downloadJson.uuid) {
          setCurrentUuid(downloadJson.uuid);
        }
      }
      
    } catch (err) {
      setError(err.message || 'Erreur lors de la génération');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const downloadQRCode = async () => {
    if (!currentUuid) {
      setError("Aucun QR code généré pour le téléchargement.");
      return;
    }

    try {
      const res = await authFetch(`${CONFIG.BASE_URL}/qr-code/${currentUuid}/download`, {
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error(`Téléchargement impossible (${res.status}).`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = res.headers.get('Content-Disposition');
      let filename = `GS1-QR-CODE-${currentUuid}.png`;
      if (cd && cd.includes('filename=')) {
        filename = cd.split('filename=')[1].split(';')[0].replace(/"/g, '');
      }

      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

    } catch (err) {
      setError(err.message || 'Erreur lors du téléchargement');
      
      if (qrCodeUrl) {
        const link = document.createElement('a');
        link.href = qrCodeUrl;
        link.download = `gs1-qr-code-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:gap-8">
        <div className="space-y-6">
          <div className={`rounded-xl border p-4 md:p-6 ${
            isDark 
              ? 'bg-dark-800 border-dark-700' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Configuration QR Code GS1 digital link</h3>
            
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="form-label">URL de base :</label>
                <input
                  type="url"
                  name="baseUrl"
                  value={formData.baseUrl}
                  onChange={handleInputChange}
                  onBlur={handleBaseUrlBlur}
                  className="form-input"
                  placeholder="https://id.gs1.org"
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>L'URL doit commencer par https://</p>
              </div>

              <div>
                <label className="form-label">Code GTIN :</label>
                <input
                  type="text"
                  name="gtin"
                  value={formData.gtin}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ex: 01234567890123"
                />
                <p className={`text-xs mt-1 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>Code produit global (GTIN-8, GTIN-12, GTIN-13 ou GTIN-14)</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Numéro de lot :</label>
                  <input
                    type="text"
                    name="lot"
                    value={formData.lot}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ex: LOT123"
                  />
                </div>

                <div>
                  <label className="form-label">Date d'expiration :</label>
                  <input
                    type="text"
                    name="exp"
                    value={formData.exp}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="YYMMDD (Ex: 251231)"
                  />
                  <p className={`text-xs mt-1 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Format: YYMMDD</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Langue :</label>
                  <select
                    name="lang"
                    value={formData.lang}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Sélectionner une langue</option>
                    <option value="fr">Français</option>
                    <option value="en">Anglais</option>
                    <option value="es">Espagnol</option>
                    <option value="de">Allemand</option>
                    <option value="it">Italien</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Type de lien :</label>
                  <select
                    name="linkType"
                    value={formData.linkType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Sélectionner un type</option>
                    <option value="product">Informations produit</option>
                    <option value="instruction">Instructions</option>
                    <option value="recall">Rappel produit</option>
                    <option value="traceability">Traçabilité</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Nombre maximum de redirections (optionnel) :</label>
                <input
                  type="number"
                  name="maxRedirect"
                  value={formData.maxRedirect}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Illimité si vide"
                  min="1"
                />
              </div>

              <div>
                <label className="form-label">Date de validité (optionnel) :</label>
                <input
                  type="datetime-local"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Date d'expiration du QR code"
                />
                <p className={`text-xs mt-1 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>Le QR code ne sera plus accessible après cette date</p>
              </div>

              {error && (
                <div className={`border px-4 py-3 rounded-lg text-sm ${
                  isDark 
                    ? 'bg-red-900/50 border-red-700 text-red-300' 
                    : 'bg-red-50 border-red-200 text-red-600'
                }`}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isGenerating}
                className="btn-primary w-full"
              >
                {isGenerating ? 'Génération...' : 'Générer le lien et QR Code'}
              </button>
            </form>
          </div>
        </div>

        <div className="lg:order-last">
          <div className={`rounded-xl border p-4 md:p-6 ${
            isDark 
              ? 'bg-dark-800 border-dark-700' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <h2 className={`text-lg md:text-xl font-semibold mb-4 md:mb-6 text-center ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Résultat</h2>
            
            <div className="min-h-[250px] md:min-h-[400px] flex items-center justify-center">
              {generatedUrl ? (
                <div className="w-full space-y-4">
                  {qrCodeUrl && (
                    <div className="text-center">
                      <img 
                        src={qrCodeUrl} 
                        alt="QR Code GS1" 
                        className="mx-auto mb-4 border border-dark-600 rounded-lg bg-white p-2"
                        style={{ maxWidth: '300px', height: 'auto' }}
                      />
                    </div>
                  )}
                  
                  <div className={`border rounded-lg p-4 ${
                    isDark 
                      ? 'bg-dark-900 border-dark-600' 
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <p className={`text-sm mb-2 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>Lien GS1 Digital Link :</p>
                    <p className={`break-all font-mono text-sm ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{generatedUrl}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      onClick={copyToClipboard}
                      className={`btn-secondary ${copySuccess ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    >
                      {copySuccess ? 'Copié !' : 'Copier le lien'}
                    </button>
                    <button
                      onClick={() => window.open(generatedUrl, '_blank')}
                      className="btn-primary"
                    >
                      Ouvrir le lien
                    </button>
                    {currentUuid && (
                      <button
                        onClick={downloadQRCode}
                        className="btn-secondary sm:col-span-2"
                      >
                        Télécharger le QR Code
                      </button>
                    )}
                  </div>

                  <div className={`border rounded-lg p-4 ${
                    isDark 
                      ? 'bg-blue-900/30 border-blue-700' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <p className={`text-sm ${
                      isDark ? 'text-blue-300' : 'text-blue-600'
                    }`}>
                      <strong>💡 Besoin de plus de design ?</strong><br/>
                      Pour personnaliser davantage votre QR code, utilisez :
                    </p>
                    <div className="mt-2 flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => navigate(`/dashboard/qr?url=${encodeURIComponent(generatedUrl)}`)}
                        className="btn-primary"
                      >
                        → QR Code personnalisé
                      </button>
                      <button
                        onClick={() => navigate(`/dashboard/ai?url=${encodeURIComponent(generatedUrl)}`)}
                        className="btn-secondary"
                      >
                        → QuikR Code
                      </button>
                    </div>

                  </div>
                </div>
              ) : (
                <div className={`text-center ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-lg flex items-center justify-center ${
                    isDark ? 'bg-dark-700' : 'bg-gray-200'
                  }`}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <p>Remplissez les informations pour générer votre lien QR Code GS1 digital link</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GS1DigitalLinkEditor;
